import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import {
  type Maybe,
  Gender,
  UserFlagsDisplayFlag,
} from '@xing-com/crate-common-graphql-types';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { Flag, type FlagVariant } from '@xing-com/flag';
import { ProfileImage } from '@xing-com/profile-image';
import { Meta } from '@xing-com/typography';

import { unscrambleId } from '../../helpers';
import { useFollowUnfollow } from '../../hooks/follow-unfollow-hook';
import { useUserLoginState } from '../../hooks/user-login-state';
import { type ArticleAuthorType } from '../../types';

import { Styled } from './styles';

const getArticlesFollowersKey = (
  intl: IntlShape,
  articlesCount: number,
  followersCount: number
): string => {
  const articlesKey =
    articlesCount === 1
      ? 'ARTICLES_ARTICLE_VIEWER_INSIDER_ARTICLE_SINGULAR'
      : 'ARTICLES_ARTICLE_VIEWER_INSIDER_ARTICLE_PLURAL';

  const followersKey =
    followersCount === 1
      ? 'ARTICLES_ARTICLE_VIEWER_CONTENT_FOLLOWER_SINGULAR'
      : 'ARTICLES_ARTICLE_VIEWER_CONTENT_FOLLOWER_PLURAL';

  return `${articlesCount} ${intl.formatMessage({
    id: articlesKey,
  })} · ${followersCount} ${intl.formatMessage({
    id: followersKey,
  })}`;
};

const getFlagVariant = (displayFlag: UserFlagsDisplayFlag): FlagVariant => {
  if (displayFlag === UserFlagsDisplayFlag.Premium) {
    return 'premium';
  }
  if (displayFlag === UserFlagsDisplayFlag.Insider) {
    return 'insider';
  }

  return 'basic';
};

type AuthorFollowOrUnfollowTrackingType = {
  PropNewsProduct?: string;
  PropActionOrigin?: string;
  EventFollow?: number;
  EventUnfollow?: number;
  PropInteractionType?: string;
  PropNewsPublisherId?: string;
};

type ArticleAuthorProfileProps = {
  author: Maybe<ArticleAuthorType>;
  publishedAt?: string;
  isFollowingAuthor?: boolean;
  onFollowingClick?: (isFollowing: boolean) => void;
  className?: string;
  position?: 'header' | 'footer';
};

const ArticleAuthorProfile: React.FC<ArticleAuthorProfileProps> = ({
  author,
  publishedAt,
  isFollowingAuthor = false,
  onFollowingClick,
  className,
  position = 'header',
}) => {
  const { track: authorFollowOrUnfollowTracking } =
    useTracking<AuthorFollowOrUnfollowTrackingType>();

  const intl = useIntl();
  const { isLoggedOut, redirectToLogin } = useUserLoginState();

  const { followAuthor, unfollowAuthor, loading } = useFollowUnfollow(author, {
    onCompleted: (isFollowed) => onFollowingClick?.(isFollowed),
  });

  if (!author) {
    return null;
  }

  const handleFollowingClick = (isFollowing: boolean): void => {
    if (isLoggedOut) {
      redirectToLogin();
      return;
    }

    if (!onFollowingClick) return;

    if (isFollowing) {
      unfollowAuthor();
    } else {
      followAuthor();
    }

    const commonTrackingParameters: AuthorFollowOrUnfollowTrackingType = {
      PropNewsProduct: 'insider_pages',
      PropActionOrigin: `news_article_${position}`,
      PropNewsPublisherId: unscrambleId(author.id ?? ''),
    };

    if (!isFollowing) {
      authorFollowOrUnfollowTracking({
        ...commonTrackingParameters,
        EventFollow: 1,
        PropInteractionType: 'news_follow',
        type: 'interaction',
        event: 'EventFollow',
      });
    } else {
      authorFollowOrUnfollowTracking({
        ...commonTrackingParameters,
        EventUnfollow: 1,
        PropInteractionType: 'news_unfollow',
        type: 'interaction',
        event: 'EventUnfollow',
      });
    }
  };

  const showFollowButton = author?.showFollowButton;
  const authorLinkAriaLabel = intl.formatMessage(
    { id: 'ARTICLES_ARTICLE_VIEWER_AUTHOR_LINK_ARIA_LABEL' },
    { name: author?.title }
  );

  return (
    <Styled.ArticleAuthorProfileWrapper
      className={className}
      data-qa="article-viewer-author-profile"
    >
      {publishedAt ? (
        <Styled.ArticleAuthorPublishedAtMeta size="small">
          <FormattedMessage
            id="ARTICLES_ARTICLE_VIEWER_PUBLISHED_AT"
            values={{
              date: intl.formatDate(publishedAt, {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              }),
            }}
          />
        </Styled.ArticleAuthorPublishedAtMeta>
      ) : null}

      <Styled.ArticleAuthorProfileContainer>
        <Styled.ArticleAuthorInfoContainer>
          <Styled.ArticleAuthorLink
            href={author?.pageURL}
            aria-hidden={true}
            tabIndex={-1}
          >
            <ProfileImage
              // @ts-expect-error FIXME: SC6
              gender={author?.gender || Gender.None}
              size="small"
              sizeConfined="large"
              src={author?.profileImage || ''}
              alt=""
              data-qa="ARTICLE-AUTHOR-PROFILE-IMAGE"
            />
          </Styled.ArticleAuthorLink>

          <Styled.ArticleAuthorInfoContentContainer>
            <Styled.ArticleAuthorTitle size="medium" sizeConfined="large">
              <span>
                <Styled.ArticleAuthorLink
                  href={author?.pageURL}
                  aria-label={authorLinkAriaLabel}
                >
                  {author?.title}
                </Styled.ArticleAuthorLink>
              </span>
              {author?.userFlag ? (
                <Flag
                  variant={getFlagVariant(author.userFlag)}
                  data-qa="article-author-user-flag"
                />
              ) : null}
            </Styled.ArticleAuthorTitle>
            <Styled.ArticleAuthorTagline size="small" sizeConfined="medium">
              {author?.tagline}
            </Styled.ArticleAuthorTagline>
            <Meta size="small" sizeConfined="medium">
              {getArticlesFollowersKey(
                intl,
                author?.metadata.publishedArticlesCount,
                author?.metadata.followersCount
              )}
            </Meta>
          </Styled.ArticleAuthorInfoContentContainer>
        </Styled.ArticleAuthorInfoContainer>

        {showFollowButton ? (
          <div>
            {isFollowingAuthor ? (
              <Styled.ArticleAuthorButton
                variant="tertiary"
                size="small"
                sizeConfined="medium"
                loading={loading}
                onClick={() => handleFollowingClick(isFollowingAuthor)}
              >
                <FormattedMessage id="ARTICLES_ARTICLE_VIEWER_FOLLOWING" />
              </Styled.ArticleAuthorButton>
            ) : (
              <Styled.ArticleAuthorButton
                variant="primary"
                size="small"
                sizeConfined="medium"
                loading={loading}
                onClick={() => handleFollowingClick(isFollowingAuthor)}
              >
                <FormattedMessage id="ARTICLES_ARTICLE_VIEWER_FOLLOW" />
              </Styled.ArticleAuthorButton>
            )}
          </div>
        ) : null}
      </Styled.ArticleAuthorProfileContainer>
    </Styled.ArticleAuthorProfileWrapper>
  );
};

export default ArticleAuthorProfile;
